import { useState } from "react";
import { useAuth, errMsg } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Warehouse, Loader2 } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBpbnZlbnRvcnklMjBsb2dpc3RpY3MlMjBnYXRlJTIwcGFzcyUyMGluZHVzdHJpYWx8ZW58MHx8fHwxNzkxNDUyNTkzfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - form */}
      <div className="w-full lg:w-[45%] flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center gap-3 mb-10">
            <div className="h-11 w-11 rounded-xl bg-sky-500 flex items-center justify-center">
              <Warehouse className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold leading-none">GateTrack</h1>
              <p className="text-[11px] text-muted-foreground mt-1 font-mono-sku">
                INVENTORY · EXPENSE · GATE PASS
              </p>
            </div>
          </div>

          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">Sign in</h2>
          <p className="text-muted-foreground text-sm mb-8">
            Access your inventory, expenses and gate pass control panel.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 bg-sky-500 hover:bg-sky-600 text-white font-semibold"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="text-xs text-muted-foreground mt-6">
            Staff accounts are created by your administrator.
          </p>
        </div>
      </div>

      {/* Right - hero */}
      <div className="hidden lg:block lg:w-[55%] relative">
        <img src={HERO} alt="Warehouse" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#090D16]/95 via-[#090D16]/70 to-sky-900/40" />
        <div className="absolute bottom-0 left-0 p-12">
          <h2 className="text-4xl font-extrabold text-white max-w-md leading-tight">
            Control every item in and out of your gate.
          </h2>
          <p className="text-slate-300 mt-4 max-w-md">
            Track stock movement, daily expenses and printable gate passes with approvals — all in one
            operational dashboard.
          </p>
        </div>
      </div>
    </div>
  );
}
